import apiClient from './ApiClient';
import i18n from '../i18n';

class StaffService {
  /**
   * Get all staff for the current hotel
   */
  async getStaff() {
    try {
      const response = await apiClient.get('/auth/staff/');
      return response.results || response;
    } catch (error) {
      console.error('Error fetching staff:', error);
      throw error;
    }
  }

  /**
   * Alias for getStaff() to maintain compatibility
   */
  async getAllStaff() {
    return this.getStaff();
  }

  /**
   * Create a new staff member
   */
  async createStaff(staffData) {
    try {
      const response = await apiClient.post('/auth/staff/', staffData);
      return response;
    } catch (error) {
      console.error('Error creating staff:', error);
      throw error;
    }
  }

  /**
   * Update an existing staff member
   */
  async updateStaff(staffId, staffData) {
    try {
      const response = await apiClient.put(`/auth/staff/${staffId}/`, staffData);
      return response;
    } catch (error) {
      console.error('Error updating staff:', error);
      throw error;
    }
  }

  /**
   * Delete/deactivate a staff member
   */
  async deleteStaff(staffId) {
    try {
      const response = await apiClient.delete(`/auth/staff/${staffId}/`);
      return response;
    } catch (error) {
      console.error('Error deleting staff:', error);
      throw error;
    }
  }

  /**
   * Get staff member details by ID
   */
  async getStaffById(staffId) {
    try {
      const response = await apiClient.get(`/auth/staff/${staffId}/`);
      return response;
    } catch (error) {
      console.error('Error fetching staff details:', error);
      throw error;
    }
  }

  /**
   * Get current user's profile and permissions
   */
  async getCurrentUserProfile() {
    // Same endpoint, same validation as ApiClient.getCurrentUser — go
    // through it so concurrent boot-time callers share one request and
    // the stored user is refreshed.
    return apiClient.getCurrentUser();
  }

  /**
   * Update current user's profile
   */
  async updateCurrentUserProfile(profileData) {
    try {
      const response = await apiClient.put('/auth/profile/', profileData);
      return response;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Get available roles for staff
   */
  getRoles() {
    // Labels are translated lazily so they follow the current UI language.
    const tr = (key) => i18n.t(`ui:staffService.roles.${key}`);
    return [
      { value: 'admin', label: tr('admin.label'), description: tr('admin.description') },
      { value: 'manager', label: tr('manager.label'), description: tr('manager.description') },
      { value: 'sales', label: tr('sales.label'), description: tr('sales.description') },
      { value: 'finance_manager', label: tr('financeManager.label'), description: tr('financeManager.description') },
      { value: 'front_desk_agent', label: tr('frontDeskAgent.label'), description: tr('frontDeskAgent.description') },
      { value: 'housekeeper', label: tr('housekeeper.label'), description: tr('housekeeper.description') },
      { value: 'maintenance', label: tr('maintenance.label'), description: tr('maintenance.description') },
    ];
  }

  /**
   * Get available departments
   */
  getDepartments() {
    return [
      'Front Desk',
      'Housekeeping',
      'Maintenance',
      'Management',
      'Sales',
      'Finance',
      'Food & Beverage',
      'Security',
      'Accounting',
      'Marketing',
    ];
  }

  /**
   * Check if user has permission for specific action
   */
  async checkPermission(action, resource) {
    try {
      const profile = await this.getCurrentUserProfile();
      
      // Simple role-based permissions
      const rolePermissions = {
        admin: ['*'], // Admin can do everything
        manager: ['view', 'create', 'update', 'manage_staff', 'manage_rooms', 'manage_rates'],
        sales: ['view', 'create', 'update_reservations', 'import_bookings'],
        finance_manager: ['view', 'manage_billing', 'view_reports', 'night_audit'],
        front_desk_agent: ['view', 'create', 'update_reservations', 'checkin', 'checkout'],
        housekeeper: ['view', 'update_room_status'],
        maintenance: ['view', 'update_room_status', 'maintenance'],
      };

      const userRole = profile.role;
      const permissions = rolePermissions[userRole] || [];

      return permissions.includes('*') || permissions.includes(action);
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }
}

export default new StaffService();